import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientProductAccess } from './useClientProductAccess';
import {
  RefreshCcw,
  ShieldCheck,
  CheckCircle2,
  Eye,
  Activity,
  GitBranch,
  X,
  Copy,
  Check,
  AlertCircle,
  TrendingUp,
  Lock,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/envelope';
import { useAuth } from '@/lib/auth/useAuth';
import { PageSpinner, ProductIntro } from '@/components/ui';
import type { ResponseEnvelope } from '@/types/api';

// ── Types ──────────────────────────────────────────────────────────────────

interface StuckPlusOverview {
  stuckCount: number;
  stuckPlusCount: number;
  avgAttemptsBeforeRecovery: number;
  revealedCount: number;
  totalSessions: number;
  totalReveals: number;
}

interface StuckSession {
  sessionId: string;
  mobileNumberMasked: string;
  journeyType: 'STUCK' | 'STUCK_PLUS';
  currentState: 'CURRENTLY_RECOVERED' | 'CURRENTLY_UNRESOLVED';
  plainMobileNumber: string | null;
  operator: string | null;
  countryCode: string | null;
  failedAttemptCount: number;
  totalAttemptCount: number;
  status: string;
  recoveryPath: string | null;
  revealed: boolean;
  revealCount: number;
  firstFailedAt: string;
  lastAttemptAt: string;
  resolvedAt: string | null;
  createdAt: string;
}

interface SessionPage {
  content: StuckSession[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface RevealResponse {
  revealId: string;
  sessionId: string;
  mobileNumber: string;
  outcome: string;
  creditsDeducted: number;
  balanceAfter: number;
}

interface JourneyStep {
  verifyId: string;
  matched: boolean;
  productType: string | null;
  serviceType: string | null;
  operatorId: string | null;
  countryCode: string | null;
  latencyMs: number | null;
  occurredAt: string;
}

// ── Design constants ───────────────────────────────────────────────────────

const DARK_GLASS = {
  background: 'linear-gradient(135deg, #050e1d 0%, #060f22 100%)',
  border: '1px solid rgba(96,165,250,0.1)',
} as const;

// ── Query hooks ────────────────────────────────────────────────────────────

function useOverview() {
  return useQuery({
    queryKey: ['stuckplus-overview'],
    queryFn: async () => unwrap(await api.get<ResponseEnvelope<StuckPlusOverview>>(Endpoints.me.stuckPlus.overview)),
    staleTime: 30_000,
  });
}

function useSessions(page: number, journeyType: string) {
  return useQuery({
    queryKey: ['stuck-sessions', page, journeyType],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, size: 20 };
      if (journeyType) params.journeyType = journeyType;
      return unwrap(await api.get<ResponseEnvelope<SessionPage>>(Endpoints.me.stuckPlus.sessions, { params }));
    },
    staleTime: 15_000,
  });
}

function useJourney(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stuck-journey', sessionId],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<JourneyStep[]>>(Endpoints.me.stuckPlus.journey(sessionId))),
    enabled,
    staleTime: 60_000,
  });
}

// ── Insight text ───────────────────────────────────────────────────────────

function insightText(session: StuckSession): string {
  const n = session.failedAttemptCount;
  if (session.journeyType === 'STUCK_PLUS') {
    // Never recovered — always unresolved
    return `Still unresolved after ${n} failed attempt${n !== 1 ? 's' : ''}.`;
  }
  // STUCK: historically recovered
  if (session.currentState === 'CURRENTLY_UNRESOLVED') {
    return 'Recovered previously, but recent verification attempts failed again.';
  }
  return `Recovered successfully after ${n} failed attempt${n !== 1 ? 's' : ''}.`;
}

// ── Journey Modal ──────────────────────────────────────────────────────────

function JourneyModal({ session, onClose }: { session: StuckSession; onClose: () => void }) {
  const journey = useJourney(session.sessionId, true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="flex w-full max-w-lg flex-col rounded-2xl" style={{ ...DARK_GLASS, maxHeight: '85vh', boxShadow: '0 0 40px rgba(96,165,250,0.12), 0 24px 48px rgba(0,0,0,0.6)' }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)' }}>
              <GitBranch className="h-4 w-4" style={{ color: '#60a5fa' }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Identity Journey</h2>
              <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{session.sessionId}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 transition-colors hover:bg-white/10">
            <X className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
          </button>
        </div>

        {/* Type badge + insight */}
        <div className="border-b px-5 py-3" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          <div className="flex flex-wrap items-center gap-2">
            <JourneyTypeBadge type={session.journeyType} />
            <CurrentStateBadge state={session.currentState} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{insightText(session)}</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {journey.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Activity className="h-5 w-5 animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
          ) : !journey.data?.length ? (
            <div className="flex h-32 flex-col items-center justify-center gap-2">
              <AlertCircle className="h-7 w-7" style={{ color: 'rgba(96,165,250,0.3)' }} />
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
                No Verify/Verify+ records yet. Journey updates as verifications occur.
              </p>
            </div>
          ) : (
            <ol className="relative ml-3 border-l" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              {journey.data.map((step, idx) => (
                <li key={step.verifyId} className="mb-5 ml-5">
                  <span
                    className="absolute -left-2 flex h-4 w-4 items-center justify-center rounded-full"
                    style={{ background: step.matched ? 'rgba(52,211,153,0.2)' : 'rgba(244,63,94,0.2)', border: `2px solid ${step.matched ? '#34d399' : '#f43f5e'}` }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      style={{ background: step.matched ? 'rgba(52,211,153,0.12)' : 'rgba(244,63,94,0.12)', color: step.matched ? '#34d399' : '#f43f5e' }}
                    >
                      {step.matched ? 'MATCHED' : 'NOT MATCHED'}
                    </span>
                    {(step.productType || step.serviceType) && (
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
                        {step.productType === 'VERIFY_N_P' ? 'Verify Number Plus' : step.productType === 'VERIFY_N' ? 'Verify Number' : step.serviceType?.replace(/_/g, ' ') ?? ''}
                      </span>
                    )}
                    <span className="ml-auto text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      Attempt #{idx + 1}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    <span>{new Date(step.occurredAt).toLocaleString()}</span>
                    {step.operatorId && <span>· {step.operatorId}</span>}
                    {step.latencyMs != null && <span>· {step.latencyMs}ms</span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Charge confirmation + Reveal modals ────────────────────────────────────

function ChargeConfirmModal({
  session,
  onConfirm,
  onClose,
}: {
  session: StuckSession;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ ...DARK_GLASS, boxShadow: '0 0 40px rgba(251,146,60,0.12), 0 24px 48px rgba(0,0,0,0.6)' }}>
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.3)' }}>
            <Eye className="h-5 w-5" style={{ color: '#fb923c' }} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Unlock Mobile Number</h2>
            <p className="mt-0.5 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              This action deducts credits from your balance and is permanently audited.
            </p>
          </div>
        </div>

        <div className="mb-4 rounded-xl p-3" style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.2)' }}>
          <p className="text-xs font-medium" style={{ color: '#fb923c' }}>
            You are unlocking the mobile number for session {session.sessionId}.
            The number will be shown once — copy it before closing.
          </p>
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Reason for unlock <span className="text-red-400">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Describe why this identity unlock is necessary (min. 10 characters)"
            rows={3}
            className="w-full resize-none rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(96,165,250,0.4)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl py-2.5 text-sm font-medium"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
          >
            Cancel
          </button>
          <button
            disabled={reason.length < 10}
            onClick={() => onConfirm(reason)}
            className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: reason.length >= 10 ? 'linear-gradient(135deg, #fb923c, #f97316)' : 'rgba(251,146,60,0.3)' }}
          >
            Unlock Identity
          </button>
        </div>
      </div>
    </div>
  );
}

function RevealResultModal({ result, onClose }: { result: RevealResponse; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(result.mobileNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6 text-center" style={{ ...DARK_GLASS, boxShadow: '0 0 40px rgba(52,211,153,0.15), 0 24px 48px rgba(0,0,0,0.6)' }}>
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)' }}>
          <CheckCircle2 className="h-6 w-6" style={{ color: '#34d399' }} />
        </div>
        <h2 className="mb-1 text-base font-semibold text-white">Identity Unlocked</h2>
        <p className="mb-5 text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
          This number will not be stored in this view. Copy it now.
        </p>

        <div className="mb-4 rounded-xl py-4" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
          <span className="text-2xl font-bold tracking-widest" style={{ color: '#34d399', fontVariantNumeric: 'tabular-nums' }}>
            {result.mobileNumber}
          </span>
        </div>

        <div className="mb-5 flex justify-center gap-4 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
          <span>{result.creditsDeducted} credits deducted</span>
          <span>·</span>
          <span>Balance: {result.balanceAfter}</span>
        </div>

        <div className="flex gap-3">
          <button
            onClick={copy}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium"
            style={{ background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 rounded-xl py-2.5 text-sm font-medium"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Journey type badge ─────────────────────────────────────────────────────

function JourneyTypeBadge({ type }: { type: 'STUCK' | 'STUCK_PLUS' }) {
  const isStuckPlus = type === 'STUCK_PLUS';
  return (
    <span
      className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{
        background: isStuckPlus ? 'rgba(251,146,60,0.15)' : 'rgba(52,211,153,0.12)',
        color: isStuckPlus ? '#fb923c' : '#34d399',
      }}
    >
      {isStuckPlus ? 'Stuck+' : 'Stuck'}
    </span>
  );
}

function CurrentStateBadge({ state }: { state: 'CURRENTLY_RECOVERED' | 'CURRENTLY_UNRESOLVED' }) {
  const isResolved = state === 'CURRENTLY_RECOVERED';
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-medium"
      style={{
        background: isResolved ? 'rgba(52,211,153,0.08)' : 'rgba(244,63,94,0.1)',
        color: isResolved ? 'rgba(52,211,153,0.7)' : 'rgba(244,63,94,0.8)',
        border: `1px solid ${isResolved ? 'rgba(52,211,153,0.2)' : 'rgba(244,63,94,0.2)'}`,
      }}
    >
      {isResolved ? 'Healthy' : 'Unresolved'}
    </span>
  );
}

// ── Session row ────────────────────────────────────────────────────────────

function SessionRow({
  session,
  isAdmin,
  onReveal,
  onViewJourney,
}: {
  session: StuckSession;
  isAdmin: boolean;
  onReveal: (s: StuckSession) => void;
  onViewJourney: (s: StuckSession) => void;
}) {
  const isStuckPlus = session.journeyType === 'STUCK_PLUS';
  const accentColor = isStuckPlus ? '#fb923c' : '#34d399';

  return (
    <div
      className="grid items-center gap-4 rounded-xl px-4 py-3.5"
      style={{
        gridTemplateColumns: 'auto 1fr auto auto auto',
        border: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      {/* Indicator dot */}
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: accentColor, boxShadow: `0 0 8px ${accentColor}` }}
      />

      {/* Mobile + insight */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold tracking-widest text-white">
            {session.revealed && session.plainMobileNumber ? session.plainMobileNumber : '************'}
          </span>
          {session.revealed && session.plainMobileNumber && (
            <Lock className="h-3 w-3" style={{ color: '#34d399' }} />
          )}
          <JourneyTypeBadge type={session.journeyType} />
          <CurrentStateBadge state={session.currentState} />
        </div>
        <p className="mt-0.5 text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {insightText(session)}
          {session.operator && <span> · {session.operator}</span>}
        </p>
      </div>

      {/* Attempts badge */}
      <span
        className="hidden shrink-0 rounded-lg px-2 py-0.5 text-xs font-semibold sm:inline"
        style={{ background: 'rgba(251,146,60,0.12)', color: '#fb923c' }}
      >
        {session.failedAttemptCount} failed
      </span>

      {/* Date */}
      <span className="hidden shrink-0 text-[11px] sm:inline" style={{ color: 'rgba(255,255,255,0.3)' }}>
        {new Date(session.firstFailedAt).toLocaleDateString()}
      </span>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={() => onViewJourney(session)}
          className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold"
          style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}
        >
          <GitBranch className="h-3 w-3" />
          Journey
        </button>
        {isAdmin && !session.revealed && (
          <button
            onClick={() => onReveal(session)}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold"
            style={{ background: 'rgba(251,146,60,0.12)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.25)' }}
          >
            <Eye className="h-3 w-3" />
            Unlock
          </button>
        )}
      </div>
    </div>
  );
}

// ── KPI Card ───────────────────────────────────────────────────────────────

function KpiCard({ label, value, color, sub }: { label: string; value: string | number; color: string; sub?: string }) {
  return (
    <div className="rounded-2xl p-4" style={{ ...DARK_GLASS, boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
      <p className="mb-1.5 text-[10px] uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)' }}>
        {label}
      </p>
      <p className="text-2xl font-bold" style={{ color }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="mt-0.5 text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{sub}</p>}
    </div>
  );
}

// ── Upsell button ──────────────────────────────────────────────────────────

function UpsellButton({ to, productCode, label }: { to: string; productCode: string; label: string }) {
  const { hasProduct } = useClientProductAccess();
  const navigate = useNavigate();
  const active = hasProduct(productCode);
  return (
    <button
      onClick={() => { if (active) navigate(to); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '6px 14px',
        fontSize: 12, fontWeight: 600,
        borderRadius: 10,
        background: active ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.05)',
        color: active ? '#8b5cf6' : 'rgba(255,255,255,0.3)',
        border: `1px solid ${active ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.1)'}`,
        cursor: active ? 'pointer' : 'default',
        transition: 'all 0.15s',
      }}
    >
      {!active && <Lock className="h-3 w-3" style={{ color: 'rgba(255,255,255,0.25)' }} />}
      {label}
    </button>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

type JourneyFilter = '' | 'STUCK_PLUS' | 'STUCK';

export function ClientRecoveryCenterPage() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = hasRole('AUTHMATECH_CLIENT_ADMIN');

  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<JourneyFilter>('');
  const [journeyTarget, setJourneyTarget] = useState<StuckSession | null>(null);
  const [chargeTarget, setChargeTarget] = useState<StuckSession | null>(null);
  const [revealResult, setRevealResult] = useState<RevealResponse | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);

  const overview = useOverview();
  const sessions = useSessions(page, filter);

  const revealMutation = useMutation({
    mutationFn: async ({ sessionId, reason }: { sessionId: string; reason: string }) =>
      unwrap(await api.post<ResponseEnvelope<RevealResponse>>(
        Endpoints.me.stuckPlus.reveal(sessionId),
        { reason }
      )),
    onSuccess: (result) => {
      setChargeTarget(null);
      setRevealResult(result);
      setRevealError(null);
      void queryClient.invalidateQueries({ queryKey: ['stuck-sessions'] });
      void queryClient.invalidateQueries({ queryKey: ['stuckplus-overview'] });
    },
    onError: (err) => {
      setChargeTarget(null);
      setRevealError(isApiError(err) ? err.firstErrorMessage : 'Failed to unlock identity.');
    },
  });

  if (overview.isLoading && !overview.data) return <PageSpinner />;

  const ov = overview.data;

  const FILTERS: { value: JourneyFilter; label: string }[] = [
    { value: '', label: 'All' },
    { value: 'STUCK_PLUS', label: 'Stuck+' },
    { value: 'STUCK', label: 'Stuck' },
  ];

  return (
    <div className="flex min-h-screen flex-col gap-6 p-6">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)' }}>
            <RefreshCcw className="h-5 w-5" style={{ color: '#60a5fa' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Authmatech Stuck+</h1>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Recovery Insights &amp; Unresolved Journey Intelligence
            </p>
          </div>
        </div>
      </div>

      <ProductIntro
        dark
        icon={RefreshCcw}
        tone="stuck"
        eyebrow="Customer Journey Recovery · Stuck & Stuck+"
        title="Win back the customers who failed verification — instead of losing them silently."
        points={['Stuck = recovered', 'Stuck+ = still blocked', 'Secure Reveal', 'No credit cost to track']}
      >
        <p>
          Verification isn’t always clean: a VPN, a flaky cellular moment, a mistyped number. Normally those
          customers just vanish. <strong>Stuck</strong> shows you who got stuck but recovered; <strong>Stuck+</strong>
          shows you who’s still blocked, so you can prioritise outreach and reconnect almost-customers.
        </p>
        <p>
          When you need to reach someone, <strong>Secure Reveal</strong> lets an authorised admin recover the real
          mobile number — gated, paid, and fully audited. Tracking stuck and recovered journeys costs nothing;
          only a Secure Reveal spends credits.
        </p>
      </ProductIntro>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiCard
          label="Stuck — Recovered"
          value={ov?.stuckCount ?? 0}
          color="#34d399"
          sub="Failed then matched"
        />
        <KpiCard
          label="Stuck+ — Unresolved"
          value={ov?.stuckPlusCount ?? 0}
          color="#fb923c"
          sub="Still failing"
        />
        <KpiCard
          label="Avg Attempts Before Recovery"
          value={ov?.avgAttemptsBeforeRecovery != null ? ov.avgAttemptsBeforeRecovery.toFixed(1) : '—'}
          color="#a78bfa"
          sub="VERIFY / Verify+ only"
        />
        <KpiCard
          label="Unlocked Mobiles"
          value={ov?.revealedCount ?? 0}
          color="#60a5fa"
          sub="Secure reveals performed"
        />
      </div>

      {/* Product explanation */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl p-5" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}>
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" style={{ color: '#34d399' }} />
            <span className="text-sm font-semibold" style={{ color: '#34d399' }}>Stuck — Recovery Insights</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Users who initially failed mobile verification but later completed a successful verification.
            Recovered after at least one failed Verify or Verify+ attempt.
          </p>
        </div>
        <div className="rounded-2xl p-5" style={{ background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.15)' }}>
          <div className="mb-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" style={{ color: '#fb923c' }} />
            <span className="text-sm font-semibold" style={{ color: '#fb923c' }}>Stuck+ — Unresolved Journeys</span>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Users who repeatedly failed mobile verification and have not yet recovered.
            At least one NOT_MATCHED attempt with no later successful match.
          </p>
        </div>
      </div>

      {/* Upsell: Connect + Target when there are unresolved journeys */}
      {(ov?.stuckPlusCount ?? 0) > 0 && (
        <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(96,165,250,0.05) 100%)', border: '1px solid rgba(139,92,246,0.2)' }}>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>
              <TrendingUp className="h-5 w-5" style={{ color: '#8b5cf6' }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">
                {(ov?.stuckPlusCount ?? 0).toLocaleString()} unresolved identity {(ov?.stuckPlusCount ?? 0) === 1 ? 'journey' : 'journeys'} eligible for recovery
              </p>
              <p className="mt-1 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Use Authmatech Connect and Target to sync these users with your CRM and build recovery audiences.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <UpsellButton to="/portal/connect" productCode="CONNECT" label="Explore Connect" />
                <UpsellButton to="/portal/target" productCode="SIGNAL" label="Explore Target" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy notice */}
      <div className="flex items-start gap-3 rounded-2xl px-5 py-4" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" style={{ color: '#60a5fa' }} />
        <div>
          <p className="text-sm font-semibold text-white">Privacy-first by design</p>
          <p className="mt-0.5 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Mobile numbers are displayed as <span className="font-mono font-bold text-white">************</span> at all times.
            Unlocking a mobile deducts credits, requires a documented reason, and is permanently audited.
            {!isAdmin && ' Only workspace admins may perform identity unlocks.'}
          </p>
        </div>
      </div>

      {/* Error banner */}
      {revealError && (
        <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.25)' }}>
          <AlertCircle className="h-4 w-4 shrink-0" style={{ color: '#f43f5e' }} />
          <p className="text-sm" style={{ color: '#f43f5e' }}>{revealError}</p>
          <button onClick={() => setRevealError(null)} className="ml-auto">
            <X className="h-4 w-4" style={{ color: '#f43f5e' }} />
          </button>
        </div>
      )}

      {/* Sessions table */}
      <div className="rounded-2xl p-6" style={{ ...DARK_GLASS, boxShadow: '0 4px 32px rgba(0,0,0,0.35)' }}>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-white">Identity Journeys</h2>
            <p className="mt-0.5 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {sessions.data?.totalElements.toLocaleString() ?? '—'} total · VERIFY and Verify+ only
            </p>
          </div>
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.value || 'all'}
                onClick={() => { setFilter(f.value); setPage(0); }}
                className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                style={{
                  background: filter === f.value ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.05)',
                  color: filter === f.value ? '#60a5fa' : 'rgba(255,255,255,0.45)',
                  border: filter === f.value ? '1px solid rgba(96,165,250,0.35)' : '1px solid rgba(255,255,255,0.07)',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {sessions.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Activity className="h-5 w-5 animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} />
          </div>
        ) : !sessions.data?.content.length ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2">
            <CheckCircle2 className="h-8 w-8" style={{ color: 'rgba(52,211,153,0.4)' }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
              No sessions found for this filter.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sessions.data.content.map((session) => (
              <SessionRow
                key={session.sessionId}
                session={session}
                isAdmin={isAdmin}
                onReveal={setChargeTarget}
                onViewJourney={setJourneyTarget}
              />
            ))}
          </div>
        )}

        {(sessions.data?.totalPages ?? 0) > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Page {(sessions.data?.number ?? 0) + 1} of {sessions.data?.totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-30"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= (sessions.data?.totalPages ?? 1) - 1}
                className="rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-30"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Journey modal */}
      {journeyTarget && (
        <JourneyModal session={journeyTarget} onClose={() => setJourneyTarget(null)} />
      )}

      {/* Charge confirmation modal */}
      {chargeTarget && (
        <ChargeConfirmModal
          session={chargeTarget}
          onClose={() => setChargeTarget(null)}
          onConfirm={(reason) => {
            revealMutation.mutate({ sessionId: chargeTarget.sessionId, reason });
          }}
        />
      )}

      {/* Reveal result modal */}
      {revealResult && (
        <RevealResultModal result={revealResult} onClose={() => setRevealResult(null)} />
      )}
    </div>
  );
}
